/**
 * DrManiQuizSlide - one DMM question slide.
 *
 * Uses DMM-native classes (dmm-slide-*, dmm-options-grid-*, dmm-btn-*) from
 * DrManiQuiz.css so the quiz keeps its own naming convention, independent of the
 * USCN quiz-slide / option-card / quiz-btn classes.
 *
 * Reads the DMM spec shape (kicker -> eyebrow, question -> title, instruction ->
 * helper, option.label/sublabel). Handles single/multi select and auto-advance.
 *
 * UX Update v2: every selection launches a botanical burst from the clicked
 * card. Answer submission adds a 2.5-second top-slot acknowledgment before the
 * quiz advances, so both the celebration and reading-counter tick are seen.
 *
 * Per-option dosing data (psb/ckaa/super_soil) is intentionally NOT rendered here —
 * those fields remain available to drManiQuizLogic (optionHint supports all three;
 * results dosing currently reads psb and super_soil).
 */
import React, { useState, useEffect, useRef } from 'react';
import type { QuizQuestion } from '../../data/drManiQuiz/types';
import DrManiOptionCard from './DrManiOptionCard';
import {
  fireQuizBotanicalBurst,
  QUIZ_ACK_DURATION_MS,
  type QuizBurstKind,
} from '../../utils/quizConfetti';

/**
 * Why this matters: every message gets a predictable 2.5-second reading
 * window before the quiz advances.
 */
const DMM_MILESTONE_QUESTIONS = new Set(['q2_grows', 'q6_why', 'q7_struggle', 'q8_experience']);

function singleSelectBurst(questionId: string): QuizBurstKind {
  if (questionId === 'q9_proudest') return 'final-answer';
  return DMM_MILESTONE_QUESTIONS.has(questionId) ? 'milestone' : 'option';
}

function submitBurst(questionId: string): QuizBurstKind {
  return DMM_MILESTONE_QUESTIONS.has(questionId) ? 'milestone' : 'submit';
}

interface DrManiQuizSlideProps {
  question: QuizQuestion;
  selected: string[];
  onAnswer: (selected: string[]) => void;
  onContinue: () => void;
  onBack?: (() => void) | undefined;
  /**
   * Called once per answer submission. Returns the celebration line to show
   * and lets the orchestrator tick the reading counter at the same moment.
   */
  onAcknowledge?: (() => string) | undefined;
  /** Lift the acknowledgment into the shared top-of-quiz message slot. */
  onAcknowledgmentChange?: ((line: string | null) => void) | undefined;
}

const DrManiQuizSlide: React.FC<DrManiQuizSlideProps> = ({
  question,
  selected,
  onAnswer,
  onContinue,
  onBack,
  onAcknowledge,
  onAcknowledgmentChange,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(selected);
  const ackTimer = useRef<number | null>(null);
  const [isAcknowledging, setIsAcknowledging] = useState(false);

  useEffect(() => {
    setSelectedOptions(selected);
  }, [selected, question.id]);

  // Reset lifted feedback and cancel pending advances when the question changes
  // or the slide unmounts mid-acknowledgment.
  useEffect(
    () => {
      // Why this matters: a question change can cancel the old timer, so the
      // new question must not inherit its disabled interaction state.
      setIsAcknowledging(false);
      onAcknowledgmentChange?.(null);
      return () => {
        if (ackTimer.current !== null) {
          window.clearTimeout(ackTimer.current);
          ackTimer.current = null;
        }
        onAcknowledgmentChange?.(null);
      };
    },
    [question.id, onAcknowledgmentChange]
  );

  const isMultiple = question.type === 'multi_select';

  /**
   * Show the micro-acknowledgment, then advance. The timer ref locks
   * synchronously while state disables the controls on the next render, so
   * rapid taps can never advance twice off one answer.
   */
  const beginAcknowledgment = (source: HTMLElement | null, burstKind: QuizBurstKind) => {
    if (ackTimer.current !== null || isAcknowledging) return;
    fireQuizBotanicalBurst(source, 'dmm', burstKind);
    const line = onAcknowledge ? onAcknowledge() : '';
    setIsAcknowledging(true);
    onAcknowledgmentChange?.(line);
    ackTimer.current = window.setTimeout(() => {
      ackTimer.current = null;
      setIsAcknowledging(false);
      // Why this matters: clear the shared slot in the same update as navigation
      // so the previous answer cannot flash beside the next question.
      onAcknowledgmentChange?.(null);
      onContinue();
    }, QUIZ_ACK_DURATION_MS);
  };

  const handleSelect = (optionValue: string, source: HTMLButtonElement) => {
    if (ackTimer.current !== null || isAcknowledging) return;
    const wasSelected = selectedOptions.includes(optionValue);
    let next: string[];
    if (isMultiple) {
      next = wasSelected
        ? selectedOptions.filter((v) => v !== optionValue)
        : [...selectedOptions, optionValue];
    } else {
      next = [optionValue];
    }
    setSelectedOptions(next);
    onAnswer(next);
    // Multi-select choices get a tiny local reward without pausing the flow.
    // Single-selects use the larger submission burst and acknowledgment.
    if (isMultiple && !wasSelected) {
      fireQuizBotanicalBurst(source, 'dmm', 'multi-option');
    }
    if (!isMultiple && question.auto_advance) {
      beginAcknowledgment(source, singleSelectBurst(question.id));
    }
  };

  const handleContinue = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (ackTimer.current !== null || isAcknowledging) return;
    beginAcknowledgment(event.currentTarget, submitBurst(question.id));
  };

  const handleBack =
    onBack &&
    (() => {
      if (ackTimer.current !== null || isAcknowledging) return;
      onBack();
    });

  const canContinue = selectedOptions.length > 0;

  // Grid columns: two is the default so no option count gets stranded in a
  // single column. Only 3 and 6 (which divide evenly into thirds) and a lone
  // option branch away from it.
  const gridClass =
    question.options.length === 3 || question.options.length === 6
      ? 'dmm-options-grid-3'
      : question.options.length === 1
        ? 'dmm-options-grid-1'
        : 'dmm-options-grid-2';

  return (
    <div className="dmm-slide">
      {/* kicker → lede → title so intro copy sits above Q1; instruction stays under the question */}
      <div className="dmm-slide-header">
        <p className="dmm-slide-eyebrow">{question.kicker}</p>
        {question.lede && (
          <p className="dmm-slide-lede">{question.lede}</p>
        )}
        {question.contract && <p className="dmm-slide-contract">{question.contract}</p>}
        <h1 className="dmm-slide-title">{question.question}</h1>
        {question.instruction && (
          <p className="dmm-slide-helper">{question.instruction}</p>
        )}
        {isMultiple && <span className="dmm-instruction"><b>Choose as many as you like</b></span>}
      </div>

      <div className="dmm-slide-content">
        <div className={`dmm-options-grid ${gridClass}`}>
          {question.options.map((option) => (
            <div key={option.value} className="dmm-option-wrap">
              <DrManiOptionCard
                option={option}
                isSelected={selectedOptions.includes(option.value)}
                isMultiple={isMultiple}
                onSelect={(source) => handleSelect(option.value, source)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="dmm-slide-navigation">
        {!question.auto_advance && (
          <button
            type="button"
            className={`dmm-btn dmm-btn-primary ${canContinue && !isAcknowledging ? 'dmm-btn-throb' : ''}`}
            onClick={handleContinue}
            disabled={!canContinue || isAcknowledging}
          >
            Continue
          </button>
        )}
        {handleBack && (
          <button type="button" className="dmm-btn dmm-btn-back" onClick={handleBack}>
            ← Back
          </button>
        )}
      </div>
    </div>
  );
};

export default DrManiQuizSlide;
